import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
    Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControlLabel, Stack, TextField, Typography
} from '@mui/material';
import { createPatient, updatePatient } from '../../services/patients';
import { t } from '../../i18n';

export default function PatientDialog({ actor, target, onClose, onSaved }) {
    const active = useRef(true);
    const lock = useRef(false);
    const [error, setError] = useState(null);
    useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
    const formik = useFormik({
        initialValues: { name: target?.name || '', email: target?.email || '', phone: '', updatePhone: false },
        validationSchema: Yup.object({
            name: Yup.string().trim().max(80, 'O texto excede o tamanho permitido.').required('Indique o nome.'),
            email: Yup.string().trim().max(256, 'O texto excede o tamanho permitido.')
                .email('Indique um email válido.').required('Indique o email.'),
            phone: Yup.string().trim().max(20, 'O texto excede o tamanho permitido.')
        }),
        onSubmit: async (values) => {
            if (lock.current) return;
            lock.current = true;
            setError(null);
            try {
                let recovered = false;
                if (target) await updatePatient(actor, target, values);
                else ({ recovered } = await createPatient(actor, values));
                if (active.current) onSaved(recovered);
            } catch {
                if (active.current) setError('Não foi possível guardar o paciente. Verifique os dados e tente novamente.');
            } finally { lock.current = false; }
        }
    });
    const field = (name, label, max, extra = {}) => <TextField fullWidth
        name={name} label={t(label)} value={formik.values[name]} onChange={formik.handleChange}
        onBlur={formik.handleBlur} disabled={formik.isSubmitting} inputProps={{ maxLength: max }}
        error={Boolean(formik.touched[name] && formik.errors[name])}
        helperText={t(formik.touched[name] && formik.errors[name])} {...extra} />;
    return <Dialog open fullWidth maxWidth="sm" onClose={() => { if (!formik.isSubmitting) onClose(); }}
        aria-labelledby="patient-dialog-title">
        <Box component="form" noValidate onSubmit={formik.handleSubmit}>
            <DialogTitle id="patient-dialog-title">{t(target ? 'Editar paciente' : 'Adicionar paciente')}</DialogTitle>
            <DialogContent>
                <Stack spacing={2.5} sx={{ pt: 1 }}>
                    {error && <Alert severity="error">{t(error)}</Alert>}
                    {field('name', 'Nome', 80, { required: true })}
                    {field('email', 'Email', 256, {
                        required: true, type: 'email', InputProps: { readOnly: Boolean(target) }
                    })}
                    {target && <Box>
                        <FormControlLabel control={<Checkbox name="updatePhone" checked={formik.values.updatePhone}
                            onChange={formik.handleChange} disabled={formik.isSubmitting} />}
                            label={t('Atualizar telefone')} />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {t('O telefone atual será mantido se não selecionar esta opção.')}
                        </Typography>
                    </Box>}
                    {(!target || formik.values.updatePhone) && field('phone', 'Telefone', 20, {
                        type: 'tel', autoComplete: 'tel',
                        helperText: formik.touched.phone && formik.errors.phone
                            ? t(formik.errors.phone) : target ? t('Introduza o novo contacto. Deixe vazio para remover o telefone.') : undefined
                    })}
                    <Typography variant="caption" color="text.secondary">
                        {t(target
                            ? 'O nome é partilhado com os restantes perfis desta conta.'
                            : 'Se a conta já existir, este perfil será associado ou reativado. Será enviada uma notificação por email.')}
                    </Typography>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button disabled={formik.isSubmitting} onClick={onClose}>{t('Cancelar')}</Button>
                <Button type="submit" variant="contained" disabled={formik.isSubmitting}>
                    {t(formik.isSubmitting ? 'A guardar…' : target ? 'Guardar alterações' : 'Adicionar paciente')}
                </Button>
            </DialogActions>
        </Box>
    </Dialog>;
}
PatientDialog.propTypes = {
    actor: PropTypes.object.isRequired, target: PropTypes.object,
    onClose: PropTypes.func.isRequired, onSaved: PropTypes.func.isRequired
};
